import { useEffect, useState } from "react";
import { api, type Dashboard, type Filter, type Point, type ValueFormat, type Widget } from "../api";
import { LineChart } from "./LineChart";

/**
 * The trend behind a KPI, shown when its card is clicked.
 *
 * It renders through the same LineChart the configured charts use, so a KPI's
 * pop-out graph looks and formats exactly like everything else on the page —
 * same axis rounding, same outlier handling, same tooltip.
 */
export function KpiChart({
  dashboard,
  metric,
  label,
  format,
  filters,
  onClose,
}: {
  dashboard: Dashboard;
  metric: string;
  label: string;
  format: ValueFormat;
  filters: Filter[];
  onClose: () => void;
}) {
  const [points, setPoints] = useState<Point[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dashboard.period_field) {
      setPoints([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .query({ dataset: dashboard.dataset, filters, period_type: "day", metrics: [metric] })
      .then((rows) => {
        if (cancelled) return;
        setPoints(
          rows
            .filter((r) => r.period_start)
            .map((r) => ({
              label: fmtDay(r.period_start!),
              value: r.values[metric] ?? null,
              period_start: r.period_start,
            })),
        );
      })
      .catch(() => !cancelled && setPoints([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [dashboard.dataset, dashboard.period_field, metric, filters]);

  const widget: Widget = {
    type: "line",
    title: `${label} over time`,
    subtitle: "Daily trend",
    format,
    metrics: [metric],
    period_type: "day",
    dimension: null,
    window: "all",
    value: null,
    series: { [metric]: points },
  };

  return (
    <div className="kpi-chart">
      <button className="kpi-chart-close" onClick={onClose} aria-label="Close chart">
        ✕
      </button>
      {loading ? (
        <div className="panel">
          <div className="empty">Loading…</div>
        </div>
      ) : !dashboard.period_field ? (
        <div className="panel">
          <div className="empty">No date field to chart this over</div>
        </div>
      ) : (
        <LineChart widget={widget} />
      )}
    </div>
  );
}

function fmtDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
  });
}
